import { categoryList } from "@ir/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/layout/PageHeader";
import { CategoriesTable } from "@/features/categories/CategoriesTable";
import { ApiError, apiFetch } from "@/lib/api";
import { authClient } from "@/lib/authClient";

export const Route = createFileRoute("/_auth/admin/categorias")({
  async beforeLoad() {
    const { data } = await authClient.getSession();
    if (data?.user.role !== "ir_admin") redirect({ to: "/insights", throw: true });
  },
  component: AdminCategoriesPage,
});

/**
 * Renombrar y eliminar cambian la categoría de los insights, así que además
 * del catálogo se invalidan el listado y las facetas de las que salen las
 * pills del catálogo.
 */
function useCategoryMutations() {
  const queryClient = useQueryClient();
  const onSuccess = async () => {
    await queryClient.invalidateQueries({ queryKey: ["categories"] });
    await queryClient.invalidateQueries({ queryKey: ["studies"] });
    await queryClient.invalidateQueries({ queryKey: ["facets"] });
  };
  const send = (method: string, url: string, body?: object) =>
    apiFetch(url, { method, ...(body ? { body: JSON.stringify(body) } : {}) });
  return {
    create: useMutation({ mutationFn: (name: string) => send("POST", "/api/categories", { name }), onSuccess }),
    rename: useMutation({
      mutationFn: ({ id, name }: { id: string; name: string }) =>
        send("PATCH", `/api/categories/${id}`, { name }),
      onSuccess,
    }),
    remove: useMutation({ mutationFn: (id: string) => send("DELETE", `/api/categories/${id}`), onSuccess }),
  };
}

function AdminCategoriesPage() {
  const { t } = useTranslation();
  const categories = useQuery({
    queryKey: ["categories"],
    queryFn: () => apiFetch("/api/categories", { schema: categoryList }),
    retry: false,
  });
  const { create, rename, remove } = useCategoryMutations();

  return (
    <>
      <PageHeader title={t("categories.title")} subtitle={t("categories.subtitle")} />
      <div className="px-8 py-6">
        <div className="overflow-x-auto rounded-[3px] border border-line bg-card">
          {categories.isError && (
            <p className="p-4 text-nota text-warn">
              {categories.error instanceof ApiError ? categories.error.message : t("common.error")}
            </p>
          )}
          {categories.isPending && <p className="p-4 text-nota text-ink3">{t("common.loading")}</p>}
          {categories.data && (
            <CategoriesTable
              categories={categories.data.items}
              onCreate={async (name) => {
                await create.mutateAsync(name);
              }}
              onRename={async (id, name) => {
                await rename.mutateAsync({ id, name });
              }}
              onDelete={async (id) => {
                await remove.mutateAsync(id);
              }}
            />
          )}
          {categories.data?.items.length === 0 && (
            <p className="p-4 text-nota text-ink3">{t("categories.empty")}</p>
          )}
        </div>
      </div>
    </>
  );
}
